/**
 * Client-side CSV validation — mirrors the backend guest-csv.parser.ts rules
 * (docs/features/guest-management/TECHNICAL_DESIGN.md §8). The server remains
 * authoritative; this exists only to give immediate, row-level feedback before
 * a file is uploaded.
 */

export interface ValidGuestRow {
  firstName: string;
  lastName: string;
  email: string;
  mobile?: string;
}

export interface CsvRowError {
  row: number;
  field: string;
  message: string;
}

export interface CsvValidationResult {
  valid: ValidGuestRow[];
  errors: CsvRowError[];
}

export const MAX_ROWS = 500;
export const MAX_FILE_BYTES = 5 * 1024 * 1024;

const INDIAN_MOBILE_RE = /^(\+91)?[6-9]\d{9}$/;
const E164_RE = /^\+[1-9]\d{7,14}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function sanitize(value: string): string {
  return value.replace(/^[=+\-@\t\r]+/, '').trim();
}

function sanitizeMobile(value: string): string {
  const trimmed = value.trim();
  const hasPlus = trimmed.startsWith('+');
  const stripped = sanitize(trimmed);
  return hasPlus ? `+${stripped}` : stripped;
}

function isValidMobile(value: string): boolean {
  const stripped = value.replace(/[\s\-()]/g, '');
  return INDIAN_MOBILE_RE.test(stripped) || E164_RE.test(stripped);
}

/** Minimal RFC-4180-ish line splitter — handles quoted cells with commas. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

const REQUIRED_COLUMNS = ['first_name', 'last_name', 'email'];

export function validateCsvText(text: string): CsvValidationResult {
  const errors: CsvRowError[] = [];
  const valid: ValidGuestRow[] = [];

  const lines = text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .filter((l) => l.trim().length > 0);

  if (lines.length === 0) {
    return { valid, errors: [{ row: 0, field: 'file', message: 'File is empty' }] };
  }

  const header = splitCsvLine(lines[0]).map((h) =>
    h.trim().toLowerCase().replace(/\s+/g, '_'),
  );
  const col = (name: string) => header.indexOf(name);

  for (const required of REQUIRED_COLUMNS) {
    if (col(required) === -1) {
      errors.push({
        row: 1,
        field: required,
        message: `Missing required column: ${required}`,
      });
    }
  }
  if (errors.length > 0) return { valid, errors };

  const seenEmails = new Set<string>();

  for (let i = 1; i < lines.length; i++) {
    const rowNum = i + 1; // 1-based; header is row 1
    if (i > MAX_ROWS) {
      errors.push({ row: rowNum, field: 'file', message: `Row limit of ${MAX_ROWS} exceeded` });
      break;
    }

    const cells = splitCsvLine(lines[i]);
    const firstName = sanitize(cells[col('first_name')] ?? '');
    const lastName = sanitize(cells[col('last_name')] ?? '');
    const email = sanitize((cells[col('email')] ?? '').toLowerCase());
    const mobileCol = col('mobile');
    const rawMobile = mobileCol >= 0 ? cells[mobileCol] : undefined;
    const mobile = rawMobile ? sanitizeMobile(rawMobile) : undefined;

    let rowHasError = false;
    const fail = (field: string, message: string) => {
      errors.push({ row: rowNum, field, message });
      rowHasError = true;
    };

    if (!firstName) fail('first_name', 'first_name is required');
    else if (firstName.length > 100) fail('first_name', 'first_name must be ≤ 100 characters');

    if (!lastName) fail('last_name', 'last_name is required');
    else if (lastName.length > 100) fail('last_name', 'last_name must be ≤ 100 characters');

    if (!email) fail('email', 'email is required');
    else if (!EMAIL_RE.test(email)) fail('email', 'email format is invalid');
    else if (seenEmails.has(email)) fail('email', `Duplicate email in file: ${email}`);

    if (mobile && !isValidMobile(mobile)) {
      fail('mobile', 'mobile must be a valid Indian or E.164 number');
    }

    if (!rowHasError) {
      seenEmails.add(email);
      valid.push({ firstName, lastName, email, mobile: mobile || undefined });
    }
  }

  return { valid, errors };
}
